/// <reference types="@types/google.maps" />
import React, { useState, useEffect, useCallback } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { Card } from '../../../components/ui/card';
import { getPublicTrackingMapData, getTrackingMapData } from '../api';
import { TrackingMapData } from '../../../types/tracking';
import { MapIcon } from 'lucide-react';
import { formatMapTimestamp } from '../../../lib/utils';

const mapContainerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%'
};

interface GoogleMapComponentProps {
    mapData: TrackingMapData;
}

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({ mapData }) => {
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService | null>(null);
    const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);
    const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
    const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(null);

    // Initialize map
    const initializeMap = useCallback((ref: HTMLDivElement | null) => {
        if (ref && window.google && !map) {
            const googleMap = new window.google.maps.Map(ref, {
                zoom: 6,
                center: { lat: 20.5937, lng: 78.9629 }, // Center of India
                mapTypeId: 'roadmap',
                zoomControl: true,
                streetViewControl: false,
                fullscreenControl: true,
                mapTypeControl: true,
            });

            const dirService = new window.google.maps.DirectionsService();
            const dirRenderer = new window.google.maps.DirectionsRenderer({
                suppressMarkers: true,
                polylineOptions: {
                    strokeColor: '#8B5CF6',
                    strokeOpacity: 0.7,
                    strokeWeight: 4
                }
            });

            dirRenderer.setMap(googleMap);
            const infoWin = new window.google.maps.InfoWindow();

            setMap(googleMap);
            setDirectionsService(dirService);
            setDirectionsRenderer(dirRenderer);
            setInfoWindow(infoWin);
        }
    }, [map]);

    // Create marker icons
    const createMarkerIcon = useCallback((type: 'origin' | 'destination' | 'marker') => {
        const icons: Record<string, { url: string; scaledSize: google.maps.Size; anchor: google.maps.Point }> = {
            origin: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="20" cy="20" r="16" fill="#10B981" stroke="#ffffff" stroke-width="3" filter="url(#shadow)"/>
            <circle cx="20" cy="20" r="10" fill="#ffffff"/>
            <path d="M20 12 L20 28 M12 20 L28 20" stroke="#10B981" stroke-width="3" stroke-linecap="round"/>
            <circle cx="20" cy="20" r="3" fill="#10B981"/>
          </svg>
        `)}`,
                scaledSize: new google.maps.Size(40, 40),
                anchor: new google.maps.Point(20, 20)
            },
            destination: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="20" cy="20" r="16" fill="#EF4444" stroke="#ffffff" stroke-width="3" filter="url(#shadow)"/>
            <circle cx="20" cy="20" r="10" fill="#ffffff"/>
            <rect x="15" y="15" width="10" height="10" fill="#EF4444" rx="2"/>
            <rect x="17" y="17" width="6" height="6" fill="#ffffff" rx="1"/>
          </svg>
        `)}`,
                scaledSize: new google.maps.Size(40, 40),
                anchor: new google.maps.Point(20, 20)
            },
            marker: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="1" dy="1" stdDeviation="2" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="16" cy="16" r="14" fill="#EF4444" stroke="#ffffff" stroke-width="2" filter="url(#shadow)"/>
          </svg>
        `)}`,
                scaledSize: new google.maps.Size(32, 32),
                anchor: new google.maps.Point(16, 16)
            }
        };
        return icons[type];
    }, []);

    // Update markers
    useEffect(() => {
        if (!map || !mapData || !infoWindow) return;

        // Clear existing markers
        markers.forEach(marker => marker.setMap(null));
        const newMarkers: google.maps.Marker[] = [];

        const addClickListener = (marker: google.maps.Marker, content: string) => {
            marker.addListener('click', () => {
                infoWindow.setContent(content);
                infoWindow.open(map, marker);
            });
        };

        // Add origin marker
        if (mapData.origin?.lat && mapData.origin?.lon) {
            const originMarker = new window.google.maps.Marker({
                position: { lat: mapData.origin.lat, lng: mapData.origin.lon },
                map: map,
                title: `Origin: ${mapData.origin.address}`,
                icon: createMarkerIcon('origin')
            });

            const infoContent = `
        <div style="padding: 8px; max-width: 200px;">
          <div style="font-weight: bold; color: #10B981; margin-bottom: 4px;">Origin</div>
          <div style="font-size: 14px;">${mapData.origin.address}</div>
          ${mapData.origin.timestamp ? `<div style="font-size: 12px; color: #666; margin-top: 4px;">${formatMapTimestamp(mapData.origin.timestamp)}</div>` : ''}
        </div>
      `;
            addClickListener(originMarker, infoContent);
            newMarkers.push(originMarker);
        }

        // Add destination marker
        if (mapData.destination?.lat && mapData.destination?.lon) {
            const destMarker = new window.google.maps.Marker({
                position: { lat: mapData.destination.lat, lng: mapData.destination.lon },
                map: map,
                title: `Destination: ${mapData.destination.address}`,
                icon: createMarkerIcon('destination')
            });

            const infoContent = `
        <div style="padding: 8px; max-width: 200px;">
          <div style="font-weight: bold; color: #EF4444; margin-bottom: 4px;">Destination</div>
          <div style="font-size: 14px;">${mapData.destination.address}</div>
          ${mapData.destination.timestamp ? `<div style="font-size: 12px; color: #666; margin-top: 4px;">${formatMapTimestamp(mapData.destination.timestamp)}</div>` : ''}
        </div>
      `;
            addClickListener(destMarker, infoContent);
            newMarkers.push(destMarker);
        }

        // Add intermediate markers
        if (mapData.markers) {
            mapData.markers.forEach((markerData, index) => {
                const marker = new window.google.maps.Marker({
                    position: { lat: markerData.lat, lng: markerData.lon },
                    map: map,
                    title: markerData.address,
                    icon: createMarkerIcon('marker')
                });

                const infoContent = `
          <div style="padding: 8px; max-width: 200px;">
            <div style="font-weight: bold; color: #EF4444; margin-bottom: 4px;">Location</div>
            <div style="font-size: 14px; margin-bottom: 4px;">${markerData.address}</div>
            ${markerData.timestamp ? `<div style="font-size: 12px; color: #666; margin-top: 4px;">${formatMapTimestamp(markerData.timestamp)}</div>` : ''}
          </div>
        `;
                addClickListener(marker, infoContent);
                newMarkers.push(marker);
            });
        }

        setMarkers(newMarkers);
    }, [map, mapData, infoWindow, createMarkerIcon]);

    // Calculate and display route
    useEffect(() => {
        if (!map || !directionsService || !directionsRenderer || !mapData) return;

        const calculateRoute = async () => {
            try {
                if (!mapData.origin?.lat || !mapData.origin?.lon || !mapData.destination?.lat || !mapData.destination?.lon) {
                    return;
                }

                const origin = { lat: mapData.origin.lat, lng: mapData.origin.lon };
                const destination = { lat: mapData.destination.lat, lng: mapData.destination.lon };

                const waypoints = mapData.markers?.map(m => ({
                    location: { lat: m.lat, lng: m.lon },
                    stopover: false
                })) || [];

                // Google Maps API limit for waypoints is 25 (plus origin/dest)
                const limitedWaypoints = waypoints.slice(0, 23);

                const request: google.maps.DirectionsRequest = {
                    origin: origin,
                    destination: destination,
                    waypoints: limitedWaypoints,
                    travelMode: google.maps.TravelMode.DRIVING,
                    optimizeWaypoints: false // Keep order as provided
                };

                directionsService.route(request, (result, status) => {
                    if (status === 'OK' && result) {
                        directionsRenderer.setDirections(result);

                        // Fit bounds
                        const bounds = new google.maps.LatLngBounds();
                        bounds.extend(origin);
                        bounds.extend(destination);
                        limitedWaypoints.forEach(wp => bounds.extend(wp.location as google.maps.LatLngLiteral));
                        map.fitBounds(bounds);
                    } else {
                        // Fallback: just origin to destination
                        directionsService.route({
                            origin: origin,
                            destination: destination,
                            travelMode: google.maps.TravelMode.DRIVING
                        }, (fbResult, fbStatus) => {
                            if (fbStatus === 'OK' && fbResult) {
                                directionsRenderer.setDirections(fbResult);
                            }
                        });
                    }
                });

            } catch (error) {
                console.error('Error calculating route:', error);
            }
        };

        // Small delay to ensure map is ready
        const timer = setTimeout(calculateRoute, 1000);
        return () => clearTimeout(timer);
    }, [map, directionsService, directionsRenderer, mapData]);

    return <div ref={initializeMap} style={mapContainerStyle} />;
};

interface TrackingMapProps {
    cnNumber: string;
    onDataRefresh?: () => void;
    isPublic?: boolean;
    token: string;
}

const TrackingMap: React.FC<TrackingMapProps> = ({ cnNumber, onDataRefresh, isPublic=false, token }) => {
    const [mapData, setMapData] = useState<TrackingMapData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    useEffect(() => {
        let isMounted = true;

        const fetchMapData = async () => {
            if (!cnNumber) return;

            try {
                setLoading(true);
                setError(null);

                let response = null;
                if(isPublic) {
                    response = await getPublicTrackingMapData(token);
                }
                else {
                    response = await getTrackingMapData(cnNumber);
                }

                if (isMounted) {
                    if (response.success) {
                        setMapData(response);
                    } else {
                        setError('Map data not available');
                    }
                }
            } catch (err) {
                if (isMounted) {
                    console.error('Error fetching map data:', err);
                    setError('Failed to load map data');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchMapData();

        return () => {
            isMounted = false;
        };
    }, [cnNumber, isPublic, token]);

    if (!apiKey) {
        return (
            <Card className="flex-1 flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="text-lg font-medium text-red-600 mb-2">Configuration Error</div>
                    <div className="text-sm text-gray-600">Google Maps API key not found</div>
                </div>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card className="flex-1 flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <div className="text-lg font-medium text-gray-900 mb-2">Loading map...</div>
                    <div className="text-sm text-gray-600">Please wait</div>
                </div>
            </Card>
        );
    }

    if (error || !mapData) {
        return (
            <Card className="flex-1 flex items-center justify-center h-full">
                <div className="text-center">
                    <MapIcon className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                    <div className="text-lg font-medium text-gray-600 mb-2">Map view unavailable</div>
                    <div className="text-sm text-gray-500">Unable to load tracking map</div>
                </div>
            </Card>
        );
    }

    return (
        <div className="h-full flex flex-col">
            <div className="flex-1 relative">
                <Wrapper apiKey={apiKey} libraries={['geometry']}>
                    <GoogleMapComponent mapData={mapData} />
                </Wrapper>
            </div>
        </div>
    );
};

export default TrackingMap;
